import { MapPin } from "@tamagui/lucide-icons";
import { action, computed, makeObservable } from "mobx";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import BaseModuleModel, {
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import { RatingManager } from "../elements/rating/RatingManager";
import LocationModel, { type MapData } from "./LocationModel";
import {
  LocationModuleStaticUI,
  LocationModuleUI,
  LocationModuleUIBlockUI,
  LocationStaticModuleUIBlockUI,
} from "./LocationModuleUI";

export type LocationModuleSnapshot = {
  numLocations: number;
  selectedLocations: string;
  selectedLocationsLatLng: { lat: number; lng: number }[];
};

export interface LocationModuleModelData
  extends ModuleModelData<LocationModuleSnapshot> {
  description: string;
  selectedLocations: string[] | null;
}

export default class LocationModuleModel extends BaseModuleModel<
  LocationModuleSnapshot,
  LocationModuleModelData
> {
  ratings = new RatingManager(this);

  constructor(stores: Stores, api: Api, data: LocationModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      description: data.description ?? "",
      selectedLocations: data.selectedLocations ?? null,
    });

    makeObservable(this);
  }

  hasHelperGuide = true;

  @action
  setDone(selectedLocationDataUids: string[]) {
    this.doneFlowEnabled = false;
    this.data.selectedLocations = selectedLocationDataUids;
    this.updateSnapshotWithoutSave();
    this.toggleDoneState();
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = this.snapshots;
  }

  @computed get snapshots(): LocationModuleSnapshot {
    const numLocations = this.dataItems.filter(
      (item): item is LocationModel => {
        return LocationModel.is(item);
      },
    ).length;

    const selectedLocations = this.data.selectedLocations
      ?.map(
        (location) =>
          this.locationItems.find(
            (locationItem) => locationItem.uid === location,
          )?.data.label,
      )
      .filter(Boolean)
      .join(", ");

    const selectedLocationsLatLng: { lat: number; lng: number }[] = [];

    this.data.selectedLocations?.forEach((location) => {
      const locationItemData = this.locationItems.find(
        (locationItem) => locationItem.uid === location,
      )?.data.mapData;

      if (locationItemData) {
        selectedLocationsLatLng.push(locationItemData);
      }
    });

    return {
      numLocations,
      selectedLocations: selectedLocations ?? "",
      selectedLocationsLatLng,
    };
  }

  @computed get mapDataItems() {
    return this.locationItems.filter(
      (
        item,
      ): item is LocationModel & {
        data: LocationModel["data"] & { mapData: MapData };
      } => {
        return !!item.data.mapData;
      },
    );
  }

  @computed get locationItems() {
    return this.dataItems.filter((item): item is LocationModel => {
      return LocationModel.is(item);
    });
  }

  @computed get hasVoted(): boolean {
    return this.ratings.getUserHasRated(this.userUid ?? "");
  }

  @computed get waitingForUsers() {
    return this.plan.users
      .filter((user) => {
        return !this.ratings.getUserHasRated(user.uid);
      })
      .map((user) => user.data.displayName);
  }

  toFireStore(): Omit<LocationModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <LocationModuleUI key={this.uid} module={this} />;
  }

  renderStatic() {
    return <LocationModuleStaticUI key={this.uid} module={this} />;
  }

  renderBlock() {
    return <LocationModuleUIBlockUI module={this} />;
  }

  renderStaticBlock() {
    return <LocationStaticModuleUIBlockUI module={this} />;
  }

  renderIcon() {
    return <MapPin />;
  }

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is LocationModuleModel {
    return module instanceof LocationModuleModel;
  }
}
